import React from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { ROUTES } from './routes';
import LandingPage from './pages/landing';
import LoginPage from './pages/login';
import LoginApplicantPage from './pages/login/applicant';
import ForgotPassword from './pages/login/forgot-password';
import ChangePassword from './pages/login/change-password';
import Step1 from './pages/step-1';
import Step2 from './pages/step-2';
import Step3 from './pages/step-3';
import CloudPayments from './pages/step-3/cloud-payments';
import PlanResult from './pages/plan-result';
import Logout from './pages/logout';
import MaintenancePage from './pages/maintenance';
import { ErrorPage } from './pages/error';
import { LoanPurpose } from './pages/loan-purpose';
import { CheckoutRoute } from './components/guards/checkout-route';
import { BankStatementsPage } from './pages/bank-statements';
import { BankStatementsOnlineBankingPage } from './pages/bank-statements-online-banking';
import { BankStatementsPDFInstructionsPage } from './pages/bank-statements-pdf-instructions';
import { HouseholdExpensesPage } from './pages/household-expenses';
import { AuthenticatedCheckoutRoute } from './components/guards/authenticated-checkout-route';
import { Contract } from './pages/contract';
import { useInitialRedirect } from './hooks/useInitialRedirect';
import { useScrollToTop } from '@payright/web-components';
import { BankStatementsPdfUploadPage } from './pages/bank-statements-pdf-upload';

const App: React.FC = () => {
  useInitialRedirect();
  useScrollToTop(useLocation);

  return (
    <Switch>
      <Route path={ROUTES.ROOT} exact component={LandingPage} />
      <Route path={ROUTES.LOGIN} exact component={LoginPage} />
      <Route path={ROUTES.LOGIN_APPLICANT} exact component={LoginApplicantPage} />
      <Route path={ROUTES.FORGOT_PASSWORD} component={ForgotPassword} />
      <Route path={ROUTES.CHANGE_PASSWORD} component={ChangePassword} />
      <Route path={ROUTES.PAYMENT_PLAN} exact>
        <Redirect to={ROUTES.PAYMENT_PLAN_CUSTOMER_DETAILS} />
      </Route>
      <CheckoutRoute
        step="Plan Purpose"
        path={ROUTES.PAYMENT_PLAN_PURPOSE}
        exact
        component={LoanPurpose}
      />
      <CheckoutRoute
        step="Customer Details"
        path={ROUTES.PAYMENT_PLAN_CUSTOMER_DETAILS}
        component={Step1}
      />
      <AuthenticatedCheckoutRoute
        step="Customer Consent"
        path={ROUTES.PAYMENT_PLAN_CUSTOMER_CONSENT}
        component={Step2}
      />
      <AuthenticatedCheckoutRoute
        step="Household Expenses"
        path={ROUTES.PAYMENT_PLAN_HOUSEHOLD_EXPENSES}
        exact
        component={HouseholdExpensesPage}
      />
      <AuthenticatedCheckoutRoute
        step="Bank Statements"
        path={ROUTES.PAYMENT_PLAN_BANK_STATEMENTS}
        exact
        component={BankStatementsPage}
      />
      <AuthenticatedCheckoutRoute
        step="Bank Statements"
        path={ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_ONLINE_BANKING}
        exact
        component={BankStatementsOnlineBankingPage}
      />
      <AuthenticatedCheckoutRoute
        step="Bank Statements"
        path={ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_PDF_INSTRUCTIONS}
        exact
        component={BankStatementsPDFInstructionsPage}
      />
      <AuthenticatedCheckoutRoute
        step="Bank Statements"
        path={ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_PDF_UPLOAD}
        exact
        component={BankStatementsPdfUploadPage}
      />
      <AuthenticatedCheckoutRoute
        step="Plan Contract"
        path={ROUTES.PAYMENT_PLAN_CONTRACT}
        component={Contract}
      />
      <AuthenticatedCheckoutRoute
        step="Payment"
        path={ROUTES.PAYMENT_PLAN_PAYMENT}
        component={Step3}
      />
      <AuthenticatedCheckoutRoute
        step="Plan Result"
        path={ROUTES.PAYMENT_PLAN_RESULT}
        component={PlanResult}
      />

      <Route path={ROUTES.LOGOUT} exact component={Logout} />
      <Route path={ROUTES.CLOUD_PAYMENTS} exact component={CloudPayments} />
      <Route path={ROUTES.MAINTENANCE} component={MaintenancePage} />
      <Route path={ROUTES.ERROR} component={ErrorPage} />
      <Route path="*">
        <ErrorPage
          title="404 - Page Not Found"
          message="The page you're looking for doesn't exist."
        />
      </Route>
    </Switch>
  );
};

export default App;
